import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

const productMediaColorMapCache = new Map();

export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    queueMicrotask(() => this.#filterMediaBySelectedColor());
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  async #filterMediaBySelectedColor() {
    const selectedColor = this.#selectedColor;
    if (!selectedColor) return;

    const mediaKeys = await this.#mediaKeysForSelectedColor(selectedColor);
    if (!this.isConnected) return;

    const hasMatchingMedia =
      mediaKeys.size > 0 &&
      Array.from(this.querySelectorAll('img')).some((image) => this.#imageMatchesMediaKeys(image, mediaKeys));
    const effectiveMediaKeys = hasMatchingMedia ? mediaKeys : new Set();
    const hasMatchingAlt =
      !hasMatchingMedia &&
      Array.from(this.querySelectorAll('img[alt]')).some((image) =>
        this.#textMatchesColor(image.getAttribute('alt') ?? '', selectedColor)
      );

    if (!hasMatchingMedia && !hasMatchingAlt) return;

    this.#filterElements('slideshow-slide', selectedColor, effectiveMediaKeys, (element, index) => {
      element.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      this.#updateInlineIndex(element, '--slide-', index);
    });
    this.#filterElements('.media-gallery__grid .product-media-container', selectedColor, effectiveMediaKeys);
    this.#filterElements('.slideshow-controls__thumbnail', selectedColor, effectiveMediaKeys, (element, index) => {
      element.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      element.setAttribute('on:click', `/select/${index}`);
    });
    this.#filterElements('.dialog-thumbnails-list__thumbnail', selectedColor, effectiveMediaKeys, (element, index) => {
      element.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      element.setAttribute('on:click', `/handleThumbnailClick/${index}`);
      element.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${index}`);
    });
    this.#filterElements('.dialog-zoomed-gallery .product-media-container', selectedColor, effectiveMediaKeys);

    this.#updateZoomTriggers();
    this.#updateSlideshowTimeline();
    this.slideshow?.setAttribute('initial-slide', '0');
    this.slideshow?.select(0, undefined, { animate: false });
  }

  #filterElements(selector, selectedColor, mediaKeys, updateElement) {
    const elements = Array.from(this.querySelectorAll(selector));
    let visibleIndex = 0;

    for (const element of elements) {
      if (!(element instanceof HTMLElement)) continue;

      if (!this.#elementMatchesSelectedColor(element, selectedColor, mediaKeys)) {
        element.remove();
        continue;
      }

      updateElement?.(element, visibleIndex);
      visibleIndex += 1;
    }
  }

  #elementMatchesSelectedColor(element, selectedColor, mediaKeys) {
    const image = element.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return false;

    if (mediaKeys.size) return this.#imageMatchesMediaKeys(image, mediaKeys);

    return this.#textMatchesColor(image.getAttribute('alt') ?? '', selectedColor);
  }

  #imageMatchesMediaKeys(image, mediaKeys) {
    if (!mediaKeys.size) return false;
    return this.#imageKeys(image).some((key) => mediaKeys.has(key));
  }

  async #mediaKeysForSelectedColor(color) {
    const assignments = await this.#productMediaColorAssignments();
    const selectedColor = this.#normalize(color);
    const mediaKeys = new Set();

    for (const [key, assignedColor] of assignments) {
      if (assignedColor === selectedColor) mediaKeys.add(key);
    }

    return mediaKeys;
  }

  async #productMediaColorAssignments() {
    const productUrl = this.#variantPicker?.dataset.productUrl;
    if (!productUrl) return new Map();

    let productJsonUrl;

    try {
      productJsonUrl = new URL(productUrl, window.location.origin);
      productJsonUrl.pathname = `${productJsonUrl.pathname.replace(/\/$/, '')}.js`;
      productJsonUrl.search = '';
      productJsonUrl.hash = '';
    } catch {
      return new Map();
    }

    const cacheKey = productJsonUrl.toString();

    if (!productMediaColorMapCache.has(cacheKey)) {
      productMediaColorMapCache.set(
        cacheKey,
        fetch(cacheKey, { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .then((product) => (product ? this.#buildProductMediaColorMap(product) : new Map()))
          .catch(() => new Map())
      );
    }

    return productMediaColorMapCache.get(cacheKey) ?? new Map();
  }

  #buildProductMediaColorMap(product) {
    const colorOptionIndex = this.#colorOptionIndex(product);
    if (colorOptionIndex === -1) return new Map();

    const imageEntries = [];
    const productImages = Array.isArray(product.images) ? product.images : [];

    for (const image of productImages) {
      const keys = this.#productImageKeys(image);
      if (keys.length) imageEntries.push({ keys: new Set(keys) });
    }

    const primaryKeysByColor = new Map();
    const variants = Array.isArray(product.variants) ? product.variants : [];

    for (const variant of variants) {
      const color = variant?.[`option${colorOptionIndex + 1}`];
      const keys = this.#productImageKeys(variant?.featured_image);
      if (!color || !keys.length) continue;

      const normalizedColor = this.#normalize(color);
      if (!primaryKeysByColor.has(normalizedColor)) primaryKeysByColor.set(normalizedColor, new Set(keys));
    }

    const colorByPrimaryKey = new Map();

    for (const [color, keys] of primaryKeysByColor) {
      for (const key of keys) {
        colorByPrimaryKey.set(key, color);
      }
    }

    const assignments = new Map();
    const colorOrder = [];
    const extraEntries = [];

    for (const entry of imageEntries) {
      const matchingKey = Array.from(entry.keys).find((key) => colorByPrimaryKey.has(key));

      if (matchingKey) {
        const normalizedColor = colorByPrimaryKey.get(matchingKey);
        for (const key of entry.keys) {
          assignments.set(key, normalizedColor);
        }
        if (!colorOrder.includes(normalizedColor)) colorOrder.push(normalizedColor);
      } else {
        extraEntries.push(entry);
      }
    }

    if (colorOrder.length && extraEntries.length) {
      const perColor = extraEntries.length / colorOrder.length;
      const blockSize = Number.isInteger(perColor) ? perColor : Math.ceil(perColor);

      if (blockSize > 0) {
        extraEntries.forEach((entry, index) => {
          const color = colorOrder[Math.min(colorOrder.length - 1, Math.floor(index / blockSize))];

          for (const key of entry.keys) {
            assignments.set(key, color);
          }
        });
      }
    }

    return assignments;
  }

  #colorOptionIndex(product) {
    const options = Array.isArray(product.options) ? product.options : [];

    return options.findIndex((option) => {
      const name = typeof option === 'string' ? option : option?.name;
      return /^colou?r$/.test(this.#normalize(name ?? ''));
    });
  }

  #productImageKeys(image) {
    const values = [];

    if (typeof image === 'string') {
      values.push(image);
    } else if (image) {
      values.push(image.src, image.url, image.original_src, image.originalSrc, image.preview_image?.src);
    }

    return values.map((value) => this.#normalizeImageUrl(value)).filter(Boolean);
  }

  #imageKeys(image) {
    const values = [image.currentSrc, image.src, image.getAttribute('src') ?? ''];
    const srcset = image.getAttribute('srcset') ?? '';

    for (const source of srcset.split(',')) {
      const url = source.trim().split(/\s+/)[0];
      if (url) values.push(url);
    }

    return values.map((value) => this.#normalizeImageUrl(value)).filter(Boolean);
  }

  #normalizeImageUrl(value) {
    if (!value) return '';

    try {
      const url = new URL(value, window.location.origin);
      let filename = decodeURIComponent(url.pathname.split('/').pop() ?? '').toLowerCase();
      filename = filename.replace(/_(?:\d+x\d*|\d*x\d+|\d+)(?:_crop_[a-z]+)?(?=\.[a-z0-9]+$)/i, '');
      return filename;
    } catch {
      return value.toString().split('?')[0]?.toLowerCase() ?? '';
    }
  }

  #updateZoomTriggers() {
    const zoomButtons = Array.from(this.querySelectorAll('[on\\:click^="/zoom/"], [on\\:click^="#zoom-dialog-"]'));

    zoomButtons.forEach((button, index) => {
      if (!(button instanceof HTMLElement)) return;

      const clickAction = button.getAttribute('on:click') ?? '';
      if (clickAction.startsWith('/zoom/')) {
        button.setAttribute('on:click', `/zoom/${index}`);
      } else {
        button.setAttribute('on:click', clickAction.replace(/\/open\/\d+$/, `/open/${index}`));
      }

      if (button.hasAttribute('on:pointerenter')) {
        button.setAttribute('on:pointerenter', `/preloadImage/${index}`);
      }
    });
  }

  #updateSlideshowTimeline() {
    const slideshow = this.slideshow;
    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    if (!slideshow || !slides.length) return;

    const timeline = slides.map((_, index) => `--slide-${index}`).join(',');
    const style = slideshow.getAttribute('style') ?? '';

    if (style.includes('--slideshow-timeline:')) {
      slideshow.setAttribute('style', style.replace(/--slideshow-timeline:[^;]*;/, `--slideshow-timeline: ${timeline};`));
    }
  }

  #updateInlineIndex(element, prefix, index) {
    const style = element.getAttribute('style') ?? '';
    if (!style.includes(prefix)) return;

    element.setAttribute('style', style.replace(new RegExp(`${prefix}\\d+`, 'g'), `${prefix}${index}`));
  }

  get #selectedColor() {
    const variantPicker = this.#variantPicker;
    if (!variantPicker) return '';

    for (const fieldset of variantPicker.querySelectorAll('fieldset')) {
      const legend = fieldset.querySelector('legend');
      const optionName = legend?.childNodes[0]?.textContent?.trim() || legend?.textContent?.trim() || '';
      if (!/^colou?r$/i.test(optionName)) continue;

      const selectedInput = fieldset.querySelector('input:checked');
      if (selectedInput instanceof HTMLInputElement) return selectedInput.value;
    }

    for (const option of variantPicker.querySelectorAll('.variant-option--dropdowns')) {
      const optionName = option.querySelector('label')?.textContent?.trim() ?? '';
      if (!/^colou?r$/i.test(optionName)) continue;

      const select = option.querySelector('select');
      if (select instanceof HTMLSelectElement) return select.value;
    }

    return '';
  }

  get #variantPicker() {
    const target = this.closest('.shopify-section, dialog') ?? document;
    return target.querySelector('variant-picker');
  }

  #textMatchesColor(value, color) {
    const normalizedValue = this.#normalize(value);
    const normalizedColor = this.#normalize(color);

    return normalizedValue.includes(`color-${normalizedColor}`);
  }

  #normalize(value) {
    return value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
